/**
 * System Pulse — AI-generierte Zusammenfassungen aller Cronjob-Aktivitaeten.
 *
 * Features:
 * - Timeline-Ansicht der Pulse-Reports (4x taeglich)
 * - Status-Level pro Report (gruen/gelb/rot)
 * - AI-Zusammenfassung mit Kategorie-Icons
 * - Aufklappbare technische Details mit Job-Tabelle
 * - Manueller Pulse-Generate fuer Admins
 * - Naechster-Pulse Countdown
 *
 * @page /entwicklung/system-pulse
 * @roles admin
 * @api /api/pulse/history, /api/pulse/generate, /api/pulse/cronjob-log
 * @i18n pulse
 */
import { useState, useEffect, useCallback } from 'react';
import useTabParam from '../hooks/useTabParam';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { PageHeader } from '../components/ui';
import { Activity, RefreshCw, ChevronRight, ChevronDown, AlertTriangle, CheckCircle2, XCircle, GitBranch } from 'lucide-react';
import DataFlowMap from './DataFlowMap';

const API = import.meta.env.VITE_API_URL || '';

// ─── Pulse-Konfiguration ──────────────────────────────────────

const PULSE_CONFIG = {
  morgen: { hour: 6,  label: 'pulse.morgen',  icon: '\u{1F305}' },
  mittag: { hour: 12, label: 'pulse.mittag',  icon: '\u{2600}\uFE0F' },
  abend:  { hour: 18, label: 'pulse.abend',   icon: '\u{1F306}' },
  nacht:  { hour: 0,  label: 'pulse.nacht',   icon: '\u{1F319}' },
};

const CATEGORY_ICONS = {
  pakete: '\u{1F4E6}',
  forecast: '\u{1F4CA}',
  cases: '\u{1F4CB}',
  mitarbeiter: '\u{1F465}',
  kunden: '\u{1F3E2}',
  system: '\u{2699}\uFE0F',
};

// ─── Hilfsfunktionen ──────────────────────────────────────────

function getNextPulse() {
  const now = new Date();
  const hours = [6, 12, 18, 24];
  const types = ['morgen', 'mittag', 'abend', 'nacht'];
  const currentHour = now.getHours();

  for (let i = 0; i < hours.length; i++) {
    if (currentHour < hours[i]) {
      const next = new Date(now);
      next.setHours(hours[i] % 24, 0, 0, 0);
      if (hours[i] === 24) next.setDate(next.getDate() + 1);
      const diffMs = next - now;
      const diffH = Math.floor(diffMs / 3600000);
      const diffM = Math.floor((diffMs % 3600000) / 60000);
      return { type: types[i], hours: diffH, minutes: diffM };
    }
  }
  const next = new Date(now);
  next.setDate(next.getDate() + 1);
  next.setHours(6, 0, 0, 0);
  const diffMs = next - now;
  return { type: 'morgen', hours: Math.floor(diffMs / 3600000), minutes: Math.floor((diffMs % 3600000) / 60000) };
}

function groupReportsByDay(reports) {
  const groups = {};
  reports.forEach(r => {
    const day = new Date(r.pulse_time).toISOString().split('T')[0];
    if (!groups[day]) groups[day] = [];
    groups[day].push(r);
  });
  return groups;
}

function formatDuration(ms) {
  if (!ms && ms !== 0) return '\u2014';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTime(isoStr) {
  return new Date(isoStr).toLocaleTimeString('de-AT', {
    hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Vienna',
  });
}

function formatDayLabel(dateStr, t) {
  const d = new Date(dateStr + 'T12:00:00');
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const todayStr = today.toISOString().split('T')[0];
  const yesterdayStr = yesterday.toISOString().split('T')[0];

  if (dateStr === todayStr) return t('pulse.today');
  if (dateStr === yesterdayStr) return t('pulse.yesterday');

  return d.toLocaleDateString('de-AT', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    timeZone: 'Europe/Vienna',
  });
}

// ─── StatusBadge ──────────────────────────────────────────────

function PulseStatusBadge({ level, failedJobs, warnings, t }) {
  if (level === 'rot') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400">
        <XCircle className="w-3.5 h-3.5" />
        {t('pulse.errors', { count: failedJobs || 1 })}
      </span>
    );
  }
  if (level === 'gelb') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
        <AlertTriangle className="w-3.5 h-3.5" />
        {t('pulse.warnings', { count: warnings || failedJobs || 1 })}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400">
      <CheckCircle2 className="w-3.5 h-3.5" />
      {t('pulse.allOk')}
    </span>
  );
}

// ─── TechDetails ──────────────────────────────────────────────

function TechDetails({ report, t }) {
  const rawData = report.raw_data;
  const jobSummary = rawData?.jobSummary || [];
  const errors = rawData?.errors || [];

  if (jobSummary.length === 0) {
    return (
      <p className="text-xs text-[var(--text-secondary)] py-2">
        {t('pulse.noJobsInPeriod', 'No jobs ran in this period.')}
      </p>
    );
  }

  return (
    <div className="space-y-4 mt-3">
      {/* Tech Summary */}
      {report.summary_tech && (
        <p className="text-xs text-[var(--text-secondary)] italic">{report.summary_tech}</p>
      )}

      {/* Job Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border)]">
              <th className="text-left text-[10px] uppercase tracking-wider text-[var(--text-secondary)] font-medium pb-2">{t('pulse.jobTable.job')}</th>
              <th className="text-right text-[10px] uppercase tracking-wider text-[var(--text-secondary)] font-medium pb-2">{t('pulse.jobTable.runs')}</th>
              <th className="text-center text-[10px] uppercase tracking-wider text-[var(--text-secondary)] font-medium pb-2">{t('pulse.jobTable.status')}</th>
              <th className="text-right text-[10px] uppercase tracking-wider text-[var(--text-secondary)] font-medium pb-2">{t('pulse.jobTable.avgDuration')}</th>
            </tr>
          </thead>
          <tbody>
            {jobSummary.map((j, i) => (
              <tr key={i} className="border-b border-[var(--border)] last:border-0">
                <td className="py-1.5 text-xs text-[var(--text-primary)] font-mono">{j.job_name}</td>
                <td className="py-1.5 text-xs text-[var(--text-primary)] text-right">{j.runs}</td>
                <td className="py-1.5 text-center">
                  {parseInt(j.errors) > 0
                    ? <span className="text-red-600 dark:text-red-400 text-xs" title={`${j.errors} errors`}>&#10060;</span>
                    : <span className="text-green-600 dark:text-green-400 text-xs">&#9989;</span>
                  }
                </td>
                <td className="py-1.5 text-xs text-[var(--text-secondary)] text-right font-mono">
                  {formatDuration(parseInt(j.avg_duration_ms))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Error Details */}
      {errors.length > 0 && (
        <div className="rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 p-3">
          <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-2">{t('pulse.errorLog')}</p>
          {errors.map((e, i) => (
            <div key={i} className="text-xs text-red-600 dark:text-red-400 mb-1 font-mono">
              {formatTime(e.started_at)} {e.job_name} — {e.error_message || e.status}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── PulseCard ────────────────────────────────────────────────

function PulseCard({ report, t }) {
  const [expanded, setExpanded] = useState(false);
  const config = PULSE_CONFIG[report.pulse_type] || {};
  const categories = report.categories || {};
  const highlights = categories._highlights || [];
  const warnings = categories._warnings || [];

  // Filter real categories (exclude _highlights, _warnings)
  const catEntries = Object.entries(categories).filter(
    ([k]) => !k.startsWith('_')
  );

  return (
    <div className="border border-[var(--border)] rounded-2xl bg-[var(--bg-card)] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-lg">{config.icon}</span>
          <div>
            <span className="text-sm font-semibold text-[var(--text-primary)]">
              {formatTime(report.pulse_time)} — {t(config.label)}
            </span>
          </div>
        </div>
        <PulseStatusBadge
          level={report.status_level}
          failedJobs={report.failed_jobs}
          warnings={report.warnings}
          t={t}
        />
      </div>

      {/* Ops Summary */}
      {report.summary_ops && (
        <p className="text-sm text-[var(--text-secondary)] mb-4 leading-relaxed">
          {report.summary_ops}
        </p>
      )}

      {/* Categories */}
      {catEntries.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {catEntries.map(([key, cat]) => (
            <div key={key} className="flex gap-2">
              <span className="text-sm mt-0.5 shrink-0">{CATEGORY_ICONS[key] || cat.icon || '\u{2699}\uFE0F'}</span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-[var(--text-primary)]">{cat.title}</p>
                <p className="text-xs text-[var(--text-secondary)] leading-relaxed line-clamp-4">{cat.text}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Highlights */}
      {highlights.length > 0 && (
        <div className="mb-3">
          {highlights.map((h, i) => (
            <p key={i} className="text-xs text-[var(--text-secondary)] mb-0.5">
              <span className="text-amber-500 mr-1">{'\u{2B50}'}</span>{h}
            </p>
          ))}
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 p-3 mb-3">
          {warnings.map((w, i) => (
            <p key={i} className="text-xs text-amber-700 dark:text-amber-400 mb-0.5">
              <AlertTriangle className="w-3 h-3 inline mr-1" />{w}
            </p>
          ))}
        </div>
      )}

      {/* Tech Details Toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors cursor-pointer"
      >
        {expanded
          ? <ChevronDown className="w-3.5 h-3.5" />
          : <ChevronRight className="w-3.5 h-3.5" />
        }
        {t('pulse.techDetails')}
      </button>

      {expanded && <TechDetails report={report} t={t} />}
    </div>
  );
}

// ─── DaySection ───────────────────────────────────────────────

function DaySection({ day, reports, t }) {
  const label = formatDayLabel(day, t);

  return (
    <div className="space-y-4">
      {/* Day Separator */}
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-[var(--border)]" />
        <span className="text-[11px] uppercase tracking-wider text-[var(--text-secondary)] font-medium shrink-0">
          {label}
        </span>
        <div className="h-px flex-1 bg-[var(--border)]" />
      </div>

      {/* Pulse Cards */}
      {reports.map(report => (
        <PulseCard key={report.id} report={report} t={t} />
      ))}
    </div>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────

export default function SystemPulse() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useTabParam('timeline');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [nextPulse, setNextPulse] = useState(getNextPulse);

  const isAdmin = user?.rolle === 'administrator' || user?.rolle === 'admin';

  const loadReports = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/pulse/history?days=7`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setReports(data);
      }
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);

  // Auto-refresh reports every 5 minutes
  useEffect(() => {
    const interval = setInterval(loadReports, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadReports]);

  // Update next-pulse countdown every minute
  useEffect(() => {
    const iv = setInterval(() => setNextPulse(getNextPulse()), 60000);
    return () => clearInterval(iv);
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/pulse/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      if (res.ok) {
        await loadReports();
      }
    } catch { /* silent */ }
    setGenerating(false);
  };

  const grouped = groupReportsByDay(reports);
  const npConfig = PULSE_CONFIG[nextPulse.type];

  return (
    <div className="p-page space-y-6">
      <PageHeader
        title={t('pulse.title')}
        subtitle={t('pulse.subtitle')}
        actions={isAdmin && (
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl
              bg-[var(--primary)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
            {generating ? t('pulse.generating') : t('pulse.generateNow')}
          </button>
        )}
      />

      {/* Tab Toggle */}
      <div className="flex gap-1 bg-[var(--bg-inset)] p-1 rounded-lg w-fit border border-[var(--border-card)]">
        {[
          { key: 'timeline', label: t('pulse.tab.timeline'), Icon: Activity },
          { key: 'datenfluss', label: t('pulse.tab.dataflow'), Icon: GitBranch },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-white dark:bg-[var(--bg-card)] shadow-sm text-[var(--text-primary)] font-semibold'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            <tab.Icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Timeline Tab */}
      {activeTab === 'timeline' && (
        <>
          {/* Next Pulse Info */}
          <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
            <Activity className="w-4 h-4" />
            <span>
              {t('pulse.nextPulse')}: {npConfig?.icon} {t(npConfig?.label)}{' '}
              {nextPulse.hours > 0 ? `${nextPulse.hours}h ` : ''}{nextPulse.minutes}min
            </span>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-5 h-5 animate-spin text-[var(--text-secondary)]" />
            </div>
          ) : reports.length === 0 ? (
            <div className="border border-[var(--border)] rounded-2xl bg-[var(--bg-card)] p-12 text-center">
              <Activity className="w-10 h-10 text-[var(--text-secondary)] mx-auto mb-3 opacity-40" />
              <p className="text-sm text-[var(--text-secondary)]">{t('pulse.noReports')}</p>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(grouped).map(([day, dayReports]) => (
                <DaySection key={day} day={day} reports={dayReports} t={t} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Datenfluss Tab */}
      {activeTab === 'datenfluss' && <DataFlowMap t={t} />}
    </div>
  );
}
